import { supabase } from '@/integrations/supabase/client';

export class ModuleGenerationService {
  
  private static splitContentIntoModules(content: string, courseId: string): Array<{
    title: string;
    content: string;
    order: number;
  }> {
    const sections = content.split(/(?=^#\s)/gm).filter(s => s.trim());
    
    if (sections.length < 4) {
      return this.splitByLength(content, 4);
    }
    
    const targetModules = Math.min(6, Math.max(4, sections.length));
    const sectionsPerModule = Math.ceil(sections.length / targetModules);
    
    const modules = [];
    for (let i = 0; i < targetModules; i++) {
      const moduleContent = sections
        .slice(i * sectionsPerModule, (i + 1) * sectionsPerModule)
        .join('\n\n');
      
      const titleMatch = moduleContent.match(/^#\s+(.+)/m);
      const title = titleMatch ? titleMatch[1].trim() : `Module ${i + 1}`;
      
      modules.push({
        title: title,
        content: moduleContent,
        order: i + 1
      });
    }
    
    return modules;
  }
  
  private static splitByLength(content: string, numModules: number) {
    const words = content.split(/\s+/);
    const wordsPerModule = Math.ceil(words.length / numModules);
    
    const modules = [];
    for (let i = 0; i < numModules; i++) {
      const moduleWords = words.slice(
        i * wordsPerModule, 
        (i + 1) * wordsPerModule
      );
      
      modules.push({
        title: `Module ${i + 1}`,
        content: moduleWords.join(' '),
        order: i + 1
      });
    }
    
    return modules;
  }
  
  private static generateQuizForModule(content: string, moduleTitle: string) {
    const keyPhrases = content
      .match(/(?:is|are|means|refers to|defines|represents)[^.!?]+[.!?]/gi)
      ?.slice(0, 5) || [];
    
    const questions = keyPhrases.map((phrase, idx) => ({
      id: `q${idx + 1}`,
      question: `What does "${moduleTitle}" teach about ${this.extractConcept(phrase)}?`,
      options: this.generateOptions(phrase),
      correctAnswer: 0,
      correctAnswerIndex: 0,
      difficulty: 'medium' as const,
      explanation: phrase.trim(),
      category: 'comprehension',
      points: 15,
      type: 'multiple-choice' as const
    }));
    
    return {
      questions,
      passThreshold: 75,
      timeLimit: 600,
      category: 'module-assessment'
    };
  }
  
  private static extractConcept(phrase: string): string {
    const match = phrase.match(/(?:is|are|means)\s+(.+?)[.,]/i);
    return match ? match[1] : 'key concepts';
  }
  
  private static generateOptions(correctPhrase: string): string[] {
    const correct = correctPhrase.trim();
    return [
      correct,
      'This is not covered in this module',
      'A different concept entirely',
      'The opposite of what is described'
    ];
  }
  
  private static generatePracticeForModule(content: string, moduleTitle: string) {
    const codeBlocks = content.match(/```[\s\S]*?```/g) || [];
    
    if (codeBlocks.length > 0) {
      return {
        title: `Practice: ${moduleTitle}`,
        description: 'Apply what you learned in this module',
        difficulty: 'medium' as const,
        estimatedTime: '30 minutes',
        tasks: [
          {
            id: 't1',
            description: 'Complete the exercise based on module concepts',
            hint: 'Review the key concepts section',
            solution: 'Solution will be revealed after attempt'
          }
        ],
        starterCode: codeBlocks[0]?.replace(/```\w*\n?|\n?```/g, '') || '',
        solution: 'See module content for detailed solution',
        hints: ['Review module examples', 'Check syntax carefully'],
        explanation: 'This exercise reinforces the core concepts from the module'
      };
    }
    
    return null;
  }
  
  public static async generateModulesForCourse(courseId: string): Promise<{
    success: boolean;
    modulesCreated: number;
    error?: string;
  }> {
    try {
      console.log(`🔄 Processing course: ${courseId}`);
      
      // Fetch all content rows for this course
      const { data: contentRows, error: contentError } = await supabase
        .from('module_content')
        .select('*')
        .eq('course_id', courseId);
      
      if (contentError || !contentRows || contentRows.length === 0) {
        return { success: false, modulesCreated: 0, error: 'No content found' };
      }
      
      // Combine all content from multiple rows
      const combinedContent = contentRows.map(row => row.content).join('\n\n');
      const modules = this.splitContentIntoModules(combinedContent, courseId);
      
      console.log(`📝 Generated ${modules.length} modules for ${courseId}`);
      
      let modulesCreated = 0;
      for (const module of modules) {
        const quiz = this.generateQuizForModule(module.content, module.title);
        const practice = this.generatePracticeForModule(module.content, module.title);
        
        const { error: insertError } = await supabase
          .from('course_modules')
          .insert({
            course_id: courseId,
            module_id: `${courseId}-module-${module.order}`,
            module_title: module.title,
            module_order: module.order,
            is_enabled: true,
            difficulty_level: 'intermediate',
            estimated_duration: Math.ceil(module.content.split(/\s+/).length / 200),
            module_config: {
              content: module.content,
              quiz: quiz,
              practice: practice
            }
          });
        
        if (!insertError) {
          modulesCreated++;
        } else {
          console.error(`❌ Error inserting module ${module.order}:`, insertError);
        }
      }
      
      console.log(`✅ Created ${modulesCreated} modules for ${courseId}`);
      
      return { success: true, modulesCreated };
      
    } catch (error) {
      console.error(`❌ Error processing ${courseId}:`, error);
      return { 
        success: false, 
        modulesCreated: 0, 
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  public static async getIncompleteCourseStats(): Promise<{
    totalIncomplete: number;
    withContent: number;
    withoutContent: number;
    courseIds: string[];
  }> {
    // First, get ALL active courses
    const { data: allCourses, error: coursesError } = await supabase
      .from('courses')
      .select('id')
      .eq('is_active', true);
    
    if (coursesError || !allCourses) {
      return { totalIncomplete: 0, withContent: 0, withoutContent: 0, courseIds: [] };
    }
    
    // Then get module counts
    const { data: moduleCounts } = await supabase
      .from('course_modules')
      .select('course_id');
    
    // Build count map (defaults to 0 for courses not in course_modules)
    const courseCounts = moduleCounts?.reduce((acc, row) => {
      acc[row.course_id] = (acc[row.course_id] || 0) + 1;
      return acc;
    }, {} as Record<string, number>) || {};
    
    // Find ALL courses with < 3 modules (including those with 0)
    const incompleteCourseIds = allCourses
      .filter(course => (courseCounts[course.id] || 0) < 3)
      .map(course => course.id);
    
    // Check which courses have content
    let withContent = 0;
    let withoutContent = 0;
    
    for (const courseId of incompleteCourseIds) {
      const { data: contentRows } = await supabase
        .from('module_content')
        .select('content')
        .eq('course_id', courseId);
      
      const totalContent = contentRows?.map(r => r.content).join('').length || 0;
      if (totalContent > 500) {
        withContent++;
      } else {
        withoutContent++;
      }
    }
    
    return {
      totalIncomplete: incompleteCourseIds.length,
      withContent,
      withoutContent,
      courseIds: incompleteCourseIds
    };
  }

  public static async generateModulesForAllIncompleteCourses(): Promise<{
    totalProcessed: number;
    totalModulesCreated: number;
    failures: string[];
  }> {
    console.log('🚀 Starting bulk module generation...');
    
    // First, get ALL active courses
    const { data: allCourses, error: coursesError } = await supabase
      .from('courses')
      .select('id')
      .eq('is_active', true);
    
    if (coursesError || !allCourses) {
      return { totalProcessed: 0, totalModulesCreated: 0, failures: [] };
    }
    
    // Then get module counts
    const { data: moduleCounts, error } = await supabase
      .from('course_modules')
      .select('course_id');
    
    if (error) {
      return { totalProcessed: 0, totalModulesCreated: 0, failures: [] };
    }
    
    // Build count map (defaults to 0 for courses not in course_modules)
    const courseCounts = moduleCounts?.reduce((acc, row) => {
      acc[row.course_id] = (acc[row.course_id] || 0) + 1;
      return acc;
    }, {} as Record<string, number>) || {};
    
    // Find ALL courses with < 3 modules (including those with 0)
    const incompleteCourses = allCourses
      .filter(course => (courseCounts[course.id] || 0) < 3)
      .map(course => course.id);
    
    console.log(`📊 Found ${incompleteCourses.length} courses to process`);
    
    let totalProcessed = 0;
    let totalModulesCreated = 0;
    const failures: string[] = [];
    
    for (const courseId of incompleteCourses) {
      const result = await this.generateModulesForCourse(courseId);
      
      totalProcessed++;
      if (result.success) {
        totalModulesCreated += result.modulesCreated;
      } else {
        failures.push(`${courseId}: ${result.error}`);
      }
      
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    console.log(`✅ Generation complete!`);
    console.log(`   Processed: ${totalProcessed} courses`);
    console.log(`   Created: ${totalModulesCreated} modules`);
    console.log(`   Failures: ${failures.length}`);
    
    return { totalProcessed, totalModulesCreated, failures };
  }
}
